import { Link } from "react-router-dom";
import { Header } from "@/components/landing/Header";
import { Footer } from "@/components/landing/Footer";
import { SeoHead } from "@/components/seo/SeoHead";
import { StructuredData } from "@/components/seo/StructuredData";
import {
  getBaseUrl,
  buildBreadcrumbSchema,
  buildOrganizationSchema,
  buildSoftwareApplicationSchema,
  buildWebPageSchema,
  buildFAQPageSchema,
} from "@/lib/seo";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";

const TITLE = "Chatbot para WhatsApp Business? Conheça agentes de IA | FabChat";
const DESCRIPTION =
  "Se você busca chatbot para WhatsApp Business, conheça a abordagem com agentes de IA da FabChat: respostas contextuais, regras configuráveis e CRM integrado.";

const BREADCRUMB = [
  { name: "Início", path: "/" },
  { name: "Chatbot para WhatsApp", path: "/whatsapp/chatbot" },
];

const FAQ_ITEMS = [
  {
    question: "A FabChat oferece chatbot para WhatsApp?",
    answer:
      "A FabChat não vende chatbot tradicional como produto. A nossa oferta é agente de IA para WhatsApp Business — uma evolução do chatbot, com inteligência artificial que mantém contexto, interpreta a intenção do cliente e responde de forma natural. É o novo método para criar relacionamento com o cliente em escala, sem depender de fluxos engessados.",
  },
  {
    question: "Qual a diferença entre chatbot e agente de IA?",
    answer:
      "Um chatbot tradicional segue uma árvore de decisão com respostas pré-definidas. Um agente de IA usa modelos de linguagem para entender o que o cliente quer, manter o contexto da conversa, adaptar a resposta às regras do negócio e encaminhar para um humano quando necessário. É a forma moderna de atender via WhatsApp.",
  },
  {
    question: "Como criar um agente de IA para WhatsApp na FabChat?",
    answer:
      "É um processo sem código: você conecta o WhatsApp Business, define a personalidade do agente, os horários, a base de conhecimento e as regras de encaminhamento. Em poucos minutos o agente está atendendo, qualificando leads e registrando tudo no CRM integrado.",
  },
  {
    question: "Posso usar um agente de IA junto com atendimento humano?",
    answer:
      "Sim. O agente de IA cuida do primeiro contato, qualificação, dúvidas frequentes e agendamentos. O humano assume a conversa sempre que faz sentido — com o histórico completo no painel — garantindo um relacionamento próximo e em escala ao mesmo tempo.",
  },
  {
    question: "Quanto custa um agente de IA para WhatsApp?",
    answer:
      "A FabChat tem planos mensais que variam conforme volume de conversas e recursos. Os valores estão atualizados na página de planos da FabChat.",
  },
];

export default function WhatsAppChatbot() {
  const baseUrl = getBaseUrl();
  const breadcrumbSchema = buildBreadcrumbSchema(baseUrl, BREADCRUMB);
  const organizationSchema = buildOrganizationSchema(baseUrl);
  const softwareSchema = buildSoftwareApplicationSchema(baseUrl, DESCRIPTION);
  const webPageSchema = buildWebPageSchema(baseUrl, { path: "/whatsapp/chatbot", title: TITLE, description: DESCRIPTION });
  const faqSchema = buildFAQPageSchema(FAQ_ITEMS);

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <SeoHead title={TITLE} description={DESCRIPTION} canonicalPath="/whatsapp/chatbot" />
      <StructuredData data={[organizationSchema, softwareSchema, breadcrumbSchema, webPageSchema, faqSchema]} />
      <Header />

      <main className="flex-1">
        <section className="container mx-auto px-4 py-16 md:py-24 max-w-4xl">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Chatbot para WhatsApp? Conheça agentes de IA
          </h1>
          <p className="text-lg text-muted-foreground mb-12">
            Quem procura chatbot para WhatsApp geralmente quer automatizar atendimento. Na FabChat, essa necessidade é atendida com <strong className="text-foreground">agentes de IA</strong> — não com um módulo de chatbot separado.
          </p>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold mb-4">O que você ganha ao automatizar no WhatsApp</h2>
            <p className="text-muted-foreground mb-4">
              Respostas automáticas, qualificação de leads, agendamento e follow-up — tudo isso a FabChat entrega por meio de <strong className="text-foreground">agentes de inteligência artificial</strong> conectados ao seu WhatsApp Business. Você define o comportamento, o tom de voz e as regras; a IA mantém o contexto da conversa e direciona o contato para vendas, suporte ou agendamento. Não vendemos “chatbot” como produto à parte: nossa oferta é o agente de IA configurável.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold mb-4">Por que agentes de IA em vez de respostas fixas</h2>
            <p className="text-muted-foreground mb-4">
              Em vez de mensagens automáticas repetidas, o agente interpreta o contexto e adapta a resposta dentro do que você configurou. Assim a conversa fica mais natural e o lead pode ser qualificado, agendado ou encaminhado para um humano no momento certo. O histórico fica no mesmo painel em que a equipe atende e em que o <Link to="/integracoes/crm" className="text-primary hover:underline">CRM integrado</Link> organiza os contatos.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold mb-4">Conheça o agente de IA para WhatsApp Business</h2>
            <p className="text-muted-foreground mb-4">
              A página dedicada ao produto explica em detalhe como funciona o agente: personalidade, regras, horários configuráveis e integração com a operação. Também oferecemos automação para <Link to="/instagram/automacao-dm" className="text-primary hover:underline">direct do Instagram</Link> e o <Link to="/integracoes/crm" className="text-primary hover:underline">CRM nativo da plataforma</Link>.
            </p>
          </section>

          <section className="flex flex-col sm:flex-row gap-4 pt-6">
            <Button asChild size="lg" className="gap-2">
              <Link to="/whatsapp/agente-ia">
                Ver agente de IA para WhatsApp <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg">
              <Link to="/planos">Ver planos</Link>
            </Button>
            <Button asChild variant="ghost" size="lg">
              <Link to="/contato">Falar com a gente</Link>
            </Button>
          </section>
        </section>
      </main>

      <Footer />
    </div>
  );
}
